import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { addPersonalCollectionWithEntity } from 'common/api/GraphApi';
import withFormState, {
  WithFormStateProps
} from 'common/components/Form/withFormState';
import { isRequired, maxLength, minLength } from 'common/forms/validators';
import trans from 'common/tools/translations/trans';

import AddToCollectionModalButtonsBar from 'website/components/user/AddToCollectionModalButtonsBar';
import { Movie, Series } from 'website/types';

import styles from './styles.module.scss';

type AddToNewCollectionFields = 'name' | 'description';

type AddToNewCollectionProps = {
  entity: Movie | Series;
  onClose: () => void;
};

const AddToNewCollection = ({
  entity,
  formErrors,
  formValues,
  onClose,
  onFieldBlur,
  onFieldChange,
  validateForm
}: WithFormStateProps<AddToNewCollectionFields> & AddToNewCollectionProps) => {
  const [name, setName] = useState(formValues.name ?? undefined);
  const [description, setDescription] = useState(
    formValues.description ?? undefined
  );

  useEffect(() => onFieldChange('name', name), [name]);
  useEffect(() => onFieldChange('description', description), [description]);

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (isValid) {
      addPersonalCollectionWithEntity({ name, description }, entity.id);
      onClose();
    }
  };

  const inputErrorMessage = formErrors.name ? (
    <span
      className={classnames('font-default', styles.addToNewCollectionError)}
    >
      {formErrors.name}
    </span>
  ) : null;

  const textareaErrorMessage = formErrors.description ? (
    <span
      className={classnames('font-default', styles.addToNewCollectionError)}
    >
      {formErrors.description}
    </span>
  ) : null;

  return (
    <>
      <div className={styles.addToCollectionContainer}>
        <fieldset
          className={classnames('font-1', styles.addToNewCollectionFieldset)}
        >
          <label className={styles.addToNewCollectionLabel}>
            {trans('userspace.my-collections.addCollectionModal.form.name')}
          </label>
          <input
            className={styles.addToNewCollectionInput}
            onBlur={onFieldBlur.bind(null, 'name')}
            onChange={e => setName(e.target.value)}
            placeholder={trans(
              'userspace.my-collections.addCollectionModal.form.namePlaceholder'
            )}
            type="text"
            value={name}
          />
          {inputErrorMessage}
        </fieldset>
        <fieldset className="font-1">
          <label className={styles.addToNewCollectionLabel}>
            {trans(
              'userspace.my-collections.addCollectionModal.form.description'
            )}
            <span
              className={classnames('font-2', styles.addToNewCollectionHint)}
            >
              {trans(
                'userspace.my-collections.addCollectionModal.form.descriptionHint'
              )}
            </span>
          </label>
          <textarea
            className={classnames('font-2', styles.addToNewCollectionTextarea)}
            onBlur={onFieldBlur.bind(null, 'description')}
            onChange={e => setDescription(e.target.value)}
            value={description}
          />
          {textareaErrorMessage}
        </fieldset>
      </div>
      <AddToCollectionModalButtonsBar
        onClick={handleSubmit}
        onClose={onClose}
      />
    </>
  );
};

export default withFormState<AddToNewCollectionProps, AddToNewCollectionFields>(
  {
    fields: ['name', 'description'],
    defaultValues: {
      name: '',
      description: ''
    },
    validators: {
      name: [
        isRequired(trans('error.isrequired')),
        minLength(
          3,
          trans(
            'userspace.my-collections.addCollectionModal.form.name.tooShortError',
            { min: 3 }
          )
        ),
        maxLength(
          100,
          trans(
            'userspace.my-collections.addCollectionModal.form.name.tooLongError',
            { max: 100 }
          )
        )
      ],
      description: [
        maxLength(
          500,
          trans(
            'userspace.my-collections.addCollectionModal.form.description.tooLongError',
            { max: 500 }
          )
        )
      ]
    }
  }
)(AddToNewCollection);
